import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { checkDiscount } from "../utils/api";
import { createWhatsAppLink } from "../config/site";

/* ----------------------------- helpers ----------------------------- */
const PLACEHOLDER =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 160 120' role='img' aria-label='No image'>
      <rect width='100%' height='100%' fill='#f2f4f8'/>
      <text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle'
        font-family='system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial' font-size='10' fill='#94a3b8'>
        No image
      </text>
    </svg>`
  );

const toNumber = (v, def = 0) => {
  const n = typeof v === "string" ? parseFloat(v.replace(/[, ]/g, "")) : Number(v);
  return Number.isFinite(n) ? n : def;
};
const moneyKES = (n) => `KSh ${toNumber(n).toLocaleString()}`;

/** try to read a color from multiple possible fields */
const readColor = (it) =>
  (it?.color ??
    it?.variantColor ??
    it?._orig?.color ??
    it?._orig?.variantColor ??
    null);

/** returns safe inline style for a swatch (accepts hex or color names) */
const swatchStyle = (c) => {
  if (!c) return {};
  // very defensive—avoid injecting anything but a color string
  const safe = String(c).slice(0, 32);
  return { background: safe };
};

const normalizeItem = (it, idx) => {
  const name = it?.name ?? it?.title ?? "Unnamed";
  const image = it?.image ?? it?.images?.[0] ?? null;
  const size = it?.size ?? it?.selectedSize ?? it?.variant ?? null;
  const color = readColor(it);
  const qty = Math.max(1, toNumber(it?.qty || it?.quantity || 1, 1));
  const unitPrice = toNumber(it?.unitPrice ?? it?.price ?? 0, 0);
  const key = it?.key ?? it?._key ?? it?.id ?? `${name}-${size ?? "_"}-${idx}`;
  const removeKey = it?.key ?? it?._key ?? it?.id ?? key;
  return {
    _orig: it,
    key,
    removeKey,
    name,
    image,
    size,
    color,
    qty,
    unitPrice,
    lineTotal: unitPrice * qty,
  };
};

/* ---------------------------------- View ---------------------------------- */
export default function Cart() {
  const navigate = useNavigate();
  const {
    items,
    remove,
    clear,
    subtotal,
    discount,
    setDiscount,
    clearDiscount,
    discountAmount,
    total,
    updateQty,
  } = useCart();

  const [code, setCode] = useState("");
  const [status, setStatus] = useState(null);

  const rows = useMemo(
    () => (Array.isArray(items) ? items.map(normalizeItem) : []),
    [items]
  );

  const apply = async () => {
    const c = code.trim();
    if (!c) return;
    try {
      const d = await checkDiscount(c);
      setDiscount(d);
      setStatus({ ok: true, msg: `Applied ${d.description || d.code}` });
    } catch {
      setStatus({ ok: false, msg: "Invalid code" });
    }
  };

  // Build a WhatsApp message for manual checkout (now includes Color)
  const waText = encodeURIComponent(
    [
      `Hello! I'd like to confirm my order.`,
      ...rows.map((r) => {
        const parts = [];
        if (r.size) parts.push(`Size ${r.size}`);
        if (r.color) parts.push(`Color ${r.color}`);
        const meta = parts.length ? ` (${parts.join(", ")})` : "";
        return `- ${r.name}${meta} × ${r.qty}`;
      }),
      `Subtotal: ${moneyKES(subtotal)}`,
      `Discount: -${moneyKES(discountAmount || 0)}`,
      `Total: ${moneyKES(total)}`,
    ].join("\n")
  );

  return (
    <main className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
      <h1 className="text-2xl font-bold tracking-tight">Your Cart</h1>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        {/* Items */}
        <section className="lg:col-span-2 space-y-3 min-h-[120px]">
          {rows.map((it) => (
            <div
              key={it.key}
              className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-3"
            >
              <div className="flex items-center gap-3">
                <div className="h-16 w-16 shrink-0 overflow-hidden rounded-xl bg-white ring-1 ring-slate-200">
                  {it.image ? (
                    <img
                      src={it.image}
                      alt={it.name}
                      className="h-full w-full object-cover"
                      onError={(e) => (e.currentTarget.src = PLACEHOLDER)}
                    />
                  ) : (
                    <img
                      src={PLACEHOLDER}
                      alt="No product"
                      className="h-full w-full object-cover"
                    />
                  )}
                </div>

                <div>
                  <div
                    className="text-sm font-semibold line-clamp-1"
                    title={it.name}
                  >
                    {it.name}
                  </div>

                  <div className="mt-0.5 text-xs text-slate-600 flex flex-wrap items-center gap-2">
                    {/* Size */}
                    {it.size && (
                      <span className="inline-flex items-center rounded-lg border border-slate-200 px-2 py-0.5">
                        Size {String(it.size)}
                      </span>
                    )}
                    {/* Color chip */}
                    {it.color && (
                      <span className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2 py-0.5">
                        <span
                          className="inline-block h-3 w-3 rounded-full ring-1 ring-slate-300"
                          style={swatchStyle(it.color)}
                          aria-label={`Color swatch ${it.color}`}
                          title={it.color}
                        />
                        <span>{String(it.color)}</span>
                      </span>
                    )}

                    {/* Qty control */}
                    <span className="hidden sm:inline">Qty:</span>
                    <div className="inline-flex items-center rounded-lg border border-slate-200">
                      <button
                        type="button"
                        className="px-2 py-1 text-xs"
                        onClick={() =>
                          updateQty?.(it.removeKey, Math.max(1, it.qty - 1))
                        }
                        aria-label="Decrease quantity"
                      >
                        −
                      </button>
                      <span className="px-2 py-1 text-xs min-w-[2ch] text-center">
                        {it.qty}
                      </span>
                      <button
                        type="button"
                        className="px-2 py-1 text-xs"
                        onClick={() => updateQty?.(it.removeKey, it.qty + 1)}
                        aria-label="Increase quantity"
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between sm:justify-end gap-4">
                <div className="text-sm font-semibold whitespace-nowrap">
                  {moneyKES(it.lineTotal)}
                </div>
                <button
                  onClick={() => remove(it.removeKey)}
                  className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs hover:bg-slate-50"
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </section>

        {/* Summary */}
        <aside className="space-y-3">
          <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">Subtotal</span>
              <span className="text-sm font-semibold">{moneyKES(subtotal)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">Discount</span>
              <span className="text-sm font-semibold">
                - {moneyKES(discountAmount)}
              </span>
            </div>
            <div className="flex items-center justify-between border-t pt-2">
              <span className="text-sm font-semibold">Total</span>
              <span className="text-sm font-bold">{moneyKES(total)}</span>
            </div>

            {/* Primary: go to manual Checkout page */}
            <button
              onClick={() => navigate("/checkout")}
              className="mt-1 w-full rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white"
            >
              Proceed to Checkout
            </button>

            {/* Quick path: confirm on WhatsApp (manual payment) */}
            <a
              href={createWhatsAppLink(decodeURIComponent(waText))}
              target="_blank"
              rel="noreferrer"
              className="w-full block text-center rounded-xl border px-4 py-2 text-sm"
            >
              Confirm via WhatsApp
            </a>
          </div>

          <DiscountBox
            code={code}
            setCode={setCode}
            discount={discount}
            apply={apply}
            clearDiscount={clearDiscount}
            status={status}
          />

          <button onClick={clear} className="text-xs text-rose-600">
            Clear Cart
          </button>
        </aside>
      </div>
    </main>
  );
}

/* small presentational piece for clarity */
function DiscountBox({ code, setCode, discount, apply, clearDiscount, status }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 space-y-2">
      <div className="text-sm font-semibold">Discount Code</div>
      <div className="flex gap-2">
        <input
          value={code}
          onChange={(e) => setCode(e.target.value)}
          className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black/20"
          placeholder="WELCOME10 / LESS500"
        />
        <button
          onClick={apply}
          className="rounded-lg bg-slate-900 px-3 py-2 text-sm text-white"
        >
          Apply
        </button>
      </div>
      {discount ? (
        <button onClick={clearDiscount} className="text-xs text-rose-600">
          Remove {discount.code}
        </button>
      ) : null}
      {status ? (
        <div className={`text-xs ${status.ok ? "text-emerald-600" : "text-rose-600"}`}>
          {status.msg}
        </div>
      ) : null}
    </div>
  );
}
